// src/pages/BulkChangeInfo.tsx
import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';

interface Account {
  id: string;
  name: string;
  phone_number: string;
  status: 'AUTHORIZED' | 'INACTIVE';
}

interface JobResponse {
  job_id: string;
}

interface JobStatus {
  task_id: string;
  state: 'PENDING' | 'SUCCESS' | 'FAILURE';
  result?: any;
  error?: string;
}

interface FieldChange {
  field: string;
  status: 'success' | 'failed' | 'skipped' | 'partial';
  error?: string | null;
  old_value?: any;
  new_value?: any;
}

interface AccountChangeResult {
  account_id: string;
  overall_status: 'success' | 'failed' | 'skipped' | 'partial';
  changes: FieldChange[];
  errors: string[];
  warnings: string[];
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Имя',
  username: 'Username',
  bio: 'Описание',
  profile_photo: 'Фото профиля',
  birthday: 'Дата рождения',
  personal_channel: 'Канал в профиле',
  privacy_phone_number: 'Приватность: телефон',
  privacy_last_seen: 'Приватность: был(а) в сети',
  privacy_profile_photo: 'Приватность: фото',
  privacy_forwards: 'Приватность: пересылка',
  privacy_calls: 'Приватность: звонки',
  privacy_birthday: 'Приватность: день рождения',
};

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  success: { label: 'Успешно', color: '#166534', bg: '#DCFCE7' },
  partial: { label: 'Частично', color: '#92400E', bg: '#FEF3C7' },
  skipped: { label: 'Без изменений', color: '#1E40AF', bg: '#DBEAFE' },
  failed: { label: 'Ошибка', color: '#991B1B', bg: '#FEE2E2' },
};

const fieldLabel = (f: string) => FIELD_LABELS[f] || f;

const JOB_STATE_LABELS: Record<string, string> = {
  PENDING: 'В обработке',
  SUCCESS: 'Завершено',
  FAILURE: 'Ошибка',
};

// Русификация служебных значений, которые бэк отдаёт по-английски
const VALUE_LABELS: Record<string, string> = {
  'Photo uploaded successfully': 'Фото загружено',
  'Photo deleted': 'Фото удалено',
  'Birthday removed': 'Дата рождения удалена',
  'Channel removed': 'Канал откреплён',
  everybody: 'Все',
  contacts: 'Контакты',
  nobody: 'Никто',
};

const valueLabel = (v: any): string => {
  if (v == null || v === '') return '—';
  return VALUE_LABELS[String(v)] || String(v);
};

// 'unchanged' — служебное значение фронта («Не менять»).
// На бэк уходят только everybody | contacts | nobody; unchanged => ключ не отправляется => None => бэк не трогает поле.
type PrivacyValue = 'unchanged' | 'everybody' | 'contacts' | 'nobody';

const AccountPickerMultiById: React.FC<{
  accounts: Account[];
  selected: string[];
  onChange: (ids: string[]) => void;
  groups?: AccountGroup[];
}> = ({ accounts, selected, onChange, groups }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const authorized = accounts.filter(acc => acc.status === 'AUTHORIZED');

  const nameToId = new Map(authorized.map(acc => [acc.name, acc.id]));
  const groupIds = (group: AccountGroup): string[] =>
    group.linked_accounts
      .map(a => nameToId.get(a.name))
      .filter((id): id is string => Boolean(id));

  const applyGroup = (group: AccountGroup) => {
    const ids = groupIds(group);
    const toAdd = ids.filter(id => !selected.includes(id));
    if (toAdd.length > 0) onChange([...selected, ...toAdd]);
  };

  const replaceWithGroup = (group: AccountGroup) => {
    onChange(groupIds(group));
  };

  const filtered = authorized.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).slice(0, 50);

  const filteredIds = filtered.map(a => a.id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selected.includes(id));
  const totalFiltered = authorized.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).length;

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter(i => i !== id) : [...selected, id]);

  const toggleAllFiltered = () => {
    if (allFilteredSelected) onChange(selected.filter(id => !filteredIds.includes(id)));
    else onChange([...selected, ...filteredIds.filter(id => !selected.includes(id))]);
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box onClick={() => setOpen(o => !o)} sx={{
        width: '100%', border: open ? '2px solid #1E40AF' : '1px solid #C4C4C4',
        borderRadius: 1, px: 1.5, py: 1, minHeight: 56, cursor: 'pointer',
        display: 'flex', alignItems: 'center', boxSizing: 'border-box', bgcolor: 'white',
        '&:hover': { borderColor: '#1E40AF' },
      }}>
        {selected.length === 0
          ? <Typography color="text.secondary">Выберите аккаунты</Typography>
          : <Typography sx={{ color: '#1E40AF', fontWeight: 500 }}>Выбрано {selected.length} из {authorized.length} аккаунтов</Typography>
        }
      </Box>
      {open && (
        <>
          <Box onClick={() => { setOpen(false); setSearch(''); }} sx={{ position: 'fixed', inset: 0, zIndex: 1200 }} />
          <Box sx={{
            position: 'absolute', top: '100%', left: 0, width: '100%', zIndex: 1300,
            bgcolor: 'white', border: '1px solid #E5E7EB', borderRadius: 1,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 0.5,
            display: 'flex', flexDirection: 'column', maxHeight: 480,
          }}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField autoFocus size="small" sx={{ flex: 1 }} placeholder="Поиск по имени или номеру..."
                value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
              <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); toggleAllFiltered(); }}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}>
                {allFilteredSelected ? 'Снять отфильтр.' : `Выбрать все (${filtered.length})`}
              </Button>
              {selected.length > 0 && (
                <Button size="small" color="error" variant="outlined" onClick={(e) => { e.stopPropagation(); onChange([]); }}
                  sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}>
                  Снять все
                </Button>
              )}
            </Box>

            {groups && groups.length > 0 && (
              <Box sx={{
                px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB',
                display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center',
                bgcolor: '#EFF6FF',
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
                  Группы:
                </Typography>
                {groups.map((group, idx) => {
                  const colors = ['#DBEAFE', '#D1FAE5', '#FEF3C7', '#FEE2E2', '#EDE9FE'];
                  const color = colors[idx % colors.length];
                  const available = groupIds(group).length;
                  const total = group.linked_accounts.length;
                  return (
                    <Chip
                      key={group.id}
                      label={available === total ? `${group.name} (${total})` : `${group.name} (${available}/${total})`}
                      size="medium"
                      variant="filled"
                      disabled={available === 0}
                      onClick={(e) => { e.stopPropagation(); applyGroup(group); }}
                      onDelete={(e) => { e.stopPropagation(); replaceWithGroup(group); }}
                      deleteIcon={
                        <Box component="span" sx={{ fontSize: '0.75rem', px: 0.25 }} title="Заменить выбор на эту группу">
                          ↺
                        </Box>
                      }
                      sx={{
                        cursor: 'pointer', bgcolor: color,
                        '&:hover': { bgcolor: '#BFDBFE' },
                        fontWeight: 500, fontSize: '0.85rem',
                      }}
                    />
                  );
                })}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontStyle: 'italic' }}>
                  клик — добавить, ↺ — заменить
                </Typography>
              </Box>
            )}

            <Box sx={{ overflowY: 'auto', flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'start' }}>
              {filtered.length === 0
                ? <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center', gridColumn: '1 / -1' }}>Ничего не найдено</Typography>
                : filtered.map(acc => (
                  <MenuItem key={acc.id} dense onClick={(e) => { e.stopPropagation(); toggle(acc.id); }}
                    selected={selected.includes(acc.id)} sx={{ py: 0.75, px: 1.5 }}>
                    <Checkbox size="small" checked={selected.includes(acc.id)} sx={{ p: 0.5, mr: 1 }} />
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={selected.includes(acc.id) ? 600 : 400} noWrap>{acc.name}</Typography>
                      {acc.phone_number && <Typography variant="caption" color="text.secondary" noWrap>{acc.phone_number}</Typography>}
                    </Box>
                  </MenuItem>
                ))
              }
              {totalFiltered > 50 && (
                <Typography sx={{ p: 1.5, fontSize: '0.75rem', color: '#6B7280', textAlign: 'center', gridColumn: '1 / -1' }}>
                  Показаны первые 50 — уточните поиск
                </Typography>
              )}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
              <Typography variant="caption" color="text.secondary">{search ? `Найдено: ${totalFiltered}` : `Авторизовано: ${authorized.length}`}</Typography>
              <Typography variant="caption" fontWeight={600} color="#1E40AF">Выбрано: {selected.length}</Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const PRIVACY_FIELDS: { key: string; label: string }[] = [
  { key: 'phone_number', label: 'Номер телефона' },
  { key: 'last_seen', label: 'Был(а) в сети' },
  { key: 'profile_photo', label: 'Фото профиля' },
  { key: 'forwards', label: 'Пересылка сообщений' },
  { key: 'calls', label: 'Звонки' },
  { key: 'birthday', label: 'Дата рождения' },
];

const BulkReport: React.FC<{ accounts: Account[]; results: AccountChangeResult[] }> = ({ accounts, results }) => {
  const nameById = new Map(accounts.map(a => [a.id, a.name]));

  const summary = results.reduce(
    (acc, r) => { acc[r.overall_status] = (acc[r.overall_status] || 0) + 1; return acc; },
    {} as Record<string, number>
  );

  return (
    <Box sx={{ mt: 2 }}>
      {/* Сводка */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {Object.entries(summary).map(([status, count]) => {
          const meta = STATUS_META[status] || STATUS_META.failed;
          return (
            <Chip
              key={status}
              label={`${meta.label}: ${count}`}
              size="small"
              sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }}
            />
          );
        })}
      </Box>

      {/* По аккаунтам */}
      {results.map((r) => {
        const meta = STATUS_META[r.overall_status] || STATUS_META.failed;
        return (
          <Box
            key={r.account_id}
            sx={{ border: '1px solid #E5E7EB', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}
          >
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
            }}>
              <Typography fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nameById.get(r.account_id) || r.account_id}
              </Typography>
              <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />
            </Box>

            <Box sx={{ px: 2, py: 1 }}>
              {r.changes.length === 0 && (
                <Typography variant="body2" color="text.secondary">Нет изменений</Typography>
              )}
              {r.changes.map((c, i) => {
                const cm = STATUS_META[c.status] || STATUS_META.failed;
                return (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cm.color, flexShrink: 0, mt: 0.5 }} />
                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 140 }}>
                      {fieldLabel(c.field)}
                    </Typography>
                    {c.status === 'failed' ? (
                      <Typography variant="body2" color="error">{c.error || 'ошибка'}</Typography>
                    ) : c.status === 'skipped' ? (
                      <Typography variant="body2" color="text.secondary">пропущено</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {c.old_value != null && c.old_value !== '' ? `${valueLabel(c.old_value)} → ` : ''}
                        <Box component="span" sx={{ color: '#111827', fontWeight: 500 }}>
                          {valueLabel(c.new_value)}
                        </Box>
                      </Typography>
                    )}
                  </Box>
                );
              })}
              {r.errors.length > 0 && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {r.errors.join('; ')}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const BulkChangeInfo: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Форма (username и прикреплённый канал намеренно отсутствуют)
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    birthday: '',         // YYYY-MM-DD
    remove_birthday: false,
    delete_current_photo: false,
  });

  // Дефолт 'unchanged' («Не менять») — на бэк уходят только реальные значения,
  // unchanged => ключ не отправляется => None => бэк не трогает поле.
  // (тот же формат, что в одиночном редактировании на странице аккаунтов)
  const [privacy, setPrivacy] = useState<Record<string, PrivacyValue>>({
    phone_number: 'unchanged', last_seen: 'unchanged', profile_photo: 'unchanged',
    forwards: 'unchanged', calls: 'unchanged', birthday: 'unchanged',
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);

  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);

  // Поллинг статуса
  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get<JobStatus>(`parse/job_status/${jobId}`);
        setJobStatus(res.data);
        if (res.data.state === 'SUCCESS' || res.data.state === 'FAILURE') {
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Ошибка проверки статуса:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [jobId]);

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAccounts();
  }, [auth?.isAuthenticated, navigate]);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/accounts');
      setAccounts(res.data.accounts || []);
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
    } catch (err: any) {
      setMessage({ text: 'Ошибка загрузки аккаунтов', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const addEmoji = (emoji: any) => {
    setForm(prev => ({ ...prev, bio: (prev.bio || '') + emoji.native }));
    setShowEmoji(false);
  };

  const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        setMessage({ text: 'Только изображения', type: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ text: 'Файл слишком большой (макс 5 МБ)', type: 'error' });
        return;
      }
      setPhotoFile(file);
    }
  };

  // Есть ли хоть одно изменение, чтобы не слать пустой запрос
  const hasAnyChange = (): boolean => {
    if (form.first_name.trim() || form.last_name.trim() || form.bio.trim()) return true;
    if (form.birthday || form.remove_birthday) return true;
    if (photoFile || form.delete_current_photo) return true;
    if (Object.values(privacy).some(v => v !== 'unchanged')) return true;
    return false;
  };

  const handleSubmit = async () => {
    if (selectedAccounts.length === 0) {
      setMessage({ text: 'Выберите хотя бы один аккаунт', type: 'error' });
      return;
    }
    if (!hasAnyChange()) {
      setMessage({ text: 'Укажите хотя бы одно изменение', type: 'error' });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();

    selectedAccounts.forEach(id => {
      formData.append('account_ids', id);
    });

    if (form.first_name.trim()) formData.append('first_name', form.first_name.trim());
    if (form.last_name.trim()) formData.append('last_name', form.last_name.trim());
    if (form.bio.trim()) formData.append('bio', form.bio.trim());

    if (form.birthday) formData.append('birthday', form.birthday);
    formData.append('remove_birthday', String(form.remove_birthday));

    formData.append('delete_current_photo', String(form.delete_current_photo));

    // Приватность: bracket-нотация, пропускаем 'unchanged' («Не менять»).
    // На бэк уходят только реальные значения => остальные поля = None => бэк их не трогает.
    // (тот же формат, что одиночное редактирование на странице аккаунтов)
    Object.entries(privacy).forEach(([key, value]) => {
      if (value !== 'unchanged') {
        formData.append(`privacy_settings[${key}]`, value);
      }
    });

    if (photoFile) {
      formData.append('profile_photo', photoFile);
    }

    try {
      const res = await api.post<JobResponse>('accounts/bulk_change_info', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJobId(res.data.job_id);
      setMessage({ text: `Задача создана! ID: ${res.data.job_id}`, type: 'success' });
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка запуска задачи', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#1E3A8A', fontWeight: 600 }}>
        Массовое изменение информации аккаунтов
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Выбор аккаунтов */}
          <Grid size={12}>
            <AccountPickerMultiById
              accounts={accounts}
              selected={selectedAccounts}
              onChange={setSelectedAccounts}
              groups={groups}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="caption" color="text.secondary">
              Все указанные ниже значения будут применены ко всем выбранным аккаунтам.
              Пустые поля пропускаются. Username и прикреплённый канал не поддерживаются
              для массового изменения.
            </Typography>
          </Grid>

          {/* Имя / Фамилия */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Имя"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              fullWidth
              inputProps={{ maxLength: 64 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Фамилия"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              fullWidth
              inputProps={{ maxLength: 64 }}
            />
          </Grid>

          {/* Bio */}
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={600} color="#1E40AF">
                Описание (bio)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EmojiEmotionsIcon />}
                onClick={() => setShowEmoji(p => !p)}
              >
                Эмодзи
              </Button>
            </Box>
            {showEmoji && (
              <Box sx={{ mb: 2, border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden' }}>
                <Picker data={data} onEmojiSelect={addEmoji} theme="light" previewPosition="none" />
              </Box>
            )}
            <TextField
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 70 }}
              helperText={`${form.bio.length}/70`}
            />
          </Grid>

          <Grid size={12}><Divider /></Grid>

          {/* Дата рождения */}
          <Grid size={12}>
            <TextField
              label="Дата рождения"
              type="date"
              value={form.birthday}
              onChange={(e) => setForm({ ...form, birthday: e.target.value })}
              fullWidth
              disabled={form.remove_birthday}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.remove_birthday}
                  onChange={(e) => setForm({ ...form, remove_birthday: e.target.checked })}
                />
              }
              label="Удалить дату рождения"
            />
          </Grid>

          <Grid size={12}><Divider /></Grid>

          {/* Настройки приватности */}
          <Grid size={12}>
            <Typography variant="subtitle1" fontWeight={600} color="#1E40AF" sx={{ mb: 1 }}>
              Настройки приватности
            </Typography>
            <Typography variant="caption" color="text.secondary">
              «Не менять» — значение по умолчанию, поле не трогается. Меняйте только нужные.
            </Typography>
          </Grid>
          {PRIVACY_FIELDS.map(({ key, label }) => (
            <Grid size={{ xs: 12, sm: 6 }} key={key}>
              <FormControl fullWidth size="small">
                <InputLabel>{label}</InputLabel>
                <Select
                  label={label}
                  value={privacy[key]}
                  onChange={(e) => setPrivacy(prev => ({ ...prev, [key]: e.target.value as PrivacyValue }))}
                >
                  <MenuItem value="unchanged">Не менять</MenuItem>
                  <MenuItem value="everybody">Все</MenuItem>
                  <MenuItem value="contacts">Контакты</MenuItem>
                  <MenuItem value="nobody">Никто</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          ))}

          <Grid size={12}><Divider /></Grid>

          {/* Фото профиля */}
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              Фото профиля (одно на все аккаунты)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              fullWidth
              sx={{ mb: 2 }}
              disabled={form.delete_current_photo}
            >
              {photoFile ? photoFile.name : 'Загрузить фото'}
              <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
            </Button>
            {photoFile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <img
                  src={URL.createObjectURL(photoFile)}
                  alt="preview"
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                />
                <IconButton onClick={() => setPhotoFile(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.delete_current_photo}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm({ ...form, delete_current_photo: checked });
                    if (checked) setPhotoFile(null);
                  }}
                />
              }
              label="Удалить текущее фото профиля"
            />
          </Grid>

          {/* Кнопка запуска */}
          <Grid size={12}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={isLoading || selectedAccounts.length === 0}
              sx={{ py: 2, bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
            >
              {isLoading ? 'Запуск...' : 'Применить ко всем выбранным'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Мониторинг задачи */}
      {jobId && (
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Статус задачи: {jobId}
          </Typography>

          {jobStatus ? (
            <>
              <Typography variant="subtitle1">
                Состояние: <strong>{JOB_STATE_LABELS[jobStatus.state] || jobStatus.state}</strong>
              </Typography>

              {jobStatus.state === 'PENDING' && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Изменение профилей...</Typography>
                </Box>
              )}

              {jobStatus.state === 'SUCCESS' && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>Задача завершена</Alert>
                  {Array.isArray(jobStatus.result?.results) ? (
                    <BulkReport accounts={accounts} results={jobStatus.result.results} />
                  ) : (
                    <Typography color="text.secondary">Нет данных по результатам</Typography>
                  )}
                </Box>
              )}

              {jobStatus.state === 'FAILURE' && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  Ошибка: {jobStatus.error || 'Неизвестная ошибка'}
                </Alert>
              )}
            </>
          ) : (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography sx={{ mt: 2 }}>Загрузка статуса...</Typography>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default BulkChangeInfo;